import React, { useState } from 'react';
import { Card, CardContent } from '@shadcn/components/ui/card';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';
import { ChevronDown, ChevronRight, RotateCcw, AlertCircle, Clock, Check, X, GitBranch, Play, ArrowRight } from 'lucide-react';
import moment from 'moment';
import StatusBadge from '../Shared/StatusBadge';
import { TRIGGER_TYPE_LABELS, EXECUTION_STATUSES, NODE_TYPES, NODE_STYLES, STEP_TYPE_LABELS, OPERATOR_LABELS } from '../../constants';

const formatDuration = (ms) => {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return `${mins}m ${secs}s`;
};

const ConditionDetail = ({ condition }) => {
  const passed = condition.result;
  return (
    <div className="flex items-center gap-2 text-xs py-0.5">
      <span className={`shrink-0 ${passed ? 'text-green-500' : 'text-red-400'}`}>
        {passed ? <Check size={10} /> : <X size={10} />}
      </span>
      <span className="text-gray-600 font-mono">{condition.field}</span>
      <span className="text-gray-400">{OPERATOR_LABELS[condition.operator] || condition.operator}</span>
      <span className="text-gray-600 font-mono">{condition.value != null ? String(condition.value) : '—'}</span>
      {condition.actualValue !== undefined && (
        <span className="text-gray-400">(was: <span className="font-mono">{String(condition.actualValue)}</span>)</span>
      )}
    </div>
  );
};

const StepRow = ({ step, index }) => {
  const [showDetails, setShowDetails] = useState(false);
  const stepDuration = step.startedAt && step.completedAt
    ? new Date(step.completedAt) - new Date(step.startedAt)
    : null;

  const typeStyle = step.type ? NODE_STYLES[step.type] : null;
  const isCondition = step.type === NODE_TYPES.CONDITION;
  const hasConditionResult = isCondition && step.conditionResult !== undefined && step.conditionResult !== null;
  const evaluatedConditions = step.evaluatedConditions || [];

  return (
    <div className="flex items-start gap-3 py-2 border-b last:border-b-0">
      <span className="text-xs text-gray-400 w-5 pt-0.5 shrink-0">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {step.type && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${typeStyle?.bgColor || 'bg-gray-100'} ${typeStyle?.textColor || 'text-gray-700'}`}>
              {step.type === NODE_TYPES.CONDITION ? <GitBranch size={10} /> : <Play size={10} />}
              {STEP_TYPE_LABELS[step.type] || step.type}
            </span>
          )}
          <span className="text-sm font-medium truncate">{step.name || 'Unknown step'}</span>
          <StatusBadge status={step.status} />
          {hasConditionResult && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold ${step.conditionResult ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
              <ArrowRight size={10} />
              {step.conditionResult ? 'Condition Met: Yes' : 'Condition Met: No'}
            </span>
          )}
          {stepDuration != null && (
            <span className="text-xs text-gray-400">{formatDuration(stepDuration)}</span>
          )}
          {isCondition && evaluatedConditions.length > 0 && (
            <button
              onClick={() => setShowDetails(prev => !prev)}
              className="text-xs text-gray-400 hover:text-gray-600 underline"
            >
              {showDetails ? 'hide' : `${evaluatedConditions.length} condition${evaluatedConditions.length !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
        {(step.summary || step.config?.summary) && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">{step.summary || step.config?.summary}</p>
        )}
        {isCondition && showDetails && evaluatedConditions.length > 0 && (
          <div className="mt-1.5 ml-1 pl-2 border-l-2 border-amber-200">
            {step.logicalOperator && evaluatedConditions.length > 1 && (
              <span className="text-xs text-amber-600 font-medium">{step.logicalOperator}</span>
            )}
            {evaluatedConditions.map((cond, i) => (
              <ConditionDetail key={i} condition={cond} />
            ))}
          </div>
        )}
        {step.error && (
          <p className="text-xs text-red-500 mt-1 truncate">{step.error.message}</p>
        )}
      </div>
    </div>
  );
};

const ExecutionCard = ({ execution, onRetry }) => {
  const [expanded, setExpanded] = useState(false);

  const canRetry = [EXECUTION_STATUSES.FAILED, EXECUTION_STATUSES.TIMEOUT].includes(execution.status);
  const steps = execution.steps || [];

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Collapsed summary row */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setExpanded(prev => !prev)}
            className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          <StatusBadge status={execution.status} />

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">
                {execution.automationName || 'Unknown rule'}
              </span>
              <Badge variant="secondary" className="text-xs shrink-0">
                {TRIGGER_TYPE_LABELS[execution.trigger?.type] || execution.trigger?.type || '-'}
              </Badge>
              {execution.trigger?.eventName && execution.trigger.eventName !== 'manual' && (
                <span className="text-xs text-gray-400 truncate">{execution.trigger.eventName}</span>
              )}
            </div>
            {execution.error && (
              <p className="text-xs text-red-500 mt-0.5 truncate flex items-center gap-1">
                <AlertCircle size={10} className="shrink-0" />
                {execution.error.message}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4 shrink-0 text-xs text-gray-400">
            {steps.length > 0 && (
              <span>{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
            )}
            {execution.duration != null && (
              <span className="flex items-center gap-1">
                <Clock size={12} />
                {formatDuration(execution.duration)}
              </span>
            )}
            {execution.startedAt && (
              <span>{moment(execution.startedAt).fromNow()}</span>
            )}
          </div>

          {canRetry && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRetry(execution._id)}
              title="Retry"
            >
              <RotateCcw size={14} />
            </Button>
          )}
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-3 ml-7 border-t pt-3">
            {steps.length > 0 ? (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Steps</h4>
                {steps.map((step, i) => (
                  <StepRow key={i} step={step} index={i} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No steps recorded</p>
            )}

            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              {execution.startedAt && (
                <span>Started: {moment(execution.startedAt).format('D MMM YYYY, h:mm:ss A')}</span>
              )}
              {execution.completedAt && (
                <span>Completed: {moment(execution.completedAt).format('D MMM YYYY, h:mm:ss A')}</span>
              )}
              {execution.correlationId && (
                <span className="font-mono">ID: {execution.correlationId}</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default React.memo(ExecutionCard);
